import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getVoterAwarenessBlogs } from '../../data/voterAwarenessBlogs';
import Header from '../Common/Header';
import './BlogDetail.css';

const BlogDetail = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  // Get blogs based on current language
  const voterAwarenessBlogs = useMemo(() => 
    getVoterAwarenessBlogs(i18n.language), 
    [i18n.language]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const foundBlog = voterAwarenessBlogs.find(b => b.slug === slug);
    
    if (foundBlog) {
      setBlog(foundBlog);
      
      // Find related blogs (same category, different blog)
      const related = voterAwarenessBlogs
        .filter(b => b.category === foundBlog.category && b.id !== foundBlog.id)
        .slice(0, 3);
      setRelatedBlogs(related);
    } else {
      navigate('/blog');
    }
  }, [slug, navigate, voterAwarenessBlogs]);

  if (!blog) {
    return (
      <div className="blog-loading">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = blog.title;

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="blog-detail-page">
      <Header />

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <span onClick={() => navigate('/')} className="breadcrumb-link">
          <i className="fas fa-home"></i> {t('blogs.breadcrumb.home')}
        </span>
        <i className="fas fa-chevron-right"></i>
        <span onClick={() => navigate('/blog')} className="breadcrumb-link">
          {t('blogs.breadcrumb.blog')}
        </span>
        <i className="fas fa-chevron-right"></i>
        <span className="breadcrumb-current">{blog.title}</span>
      </div>

      <div className="blog-detail-container">
        {/* Blog Header */}
        <div className="blog-header">
          <div className="blog-header-content">
            <span className="category-badge">{blog.category}</span>
            <h1 className="blog-title">{blog.title}</h1>
            <p className="blog-subtitle">{blog.subtitle}</p>
            
            <div className="blog-meta-bar">
              <div className="meta-left">
                <span className="author">
                  <i className="fas fa-user-circle"></i>
                  {blog.author}
                </span>
                <span className="date">
                  <i className="far fa-calendar"></i>
                  {blog.date}
                </span>
                <span className="read-time">
                  <i className="far fa-clock"></i>
                  {blog.readTime}
                </span>
              </div>
              
              <div className="share-buttons">
                <span className="share-label">{t('blogs.detail.share')}:</span>
                <button onClick={() => handleShare('facebook')} className="share-btn facebook">
                  <i className="fab fa-facebook-f"></i>
                </button>
                <button onClick={() => handleShare('twitter')} className="share-btn twitter">
                  <i className="fab fa-twitter"></i>
                </button>
                <button onClick={() => handleShare('whatsapp')} className="share-btn whatsapp">
                  <i className="fab fa-whatsapp"></i>
                </button>
                <button onClick={() => handleShare('linkedin')} className="share-btn linkedin">
                  <i className="fab fa-linkedin-in"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="blog-featured-image">
          <img src={blog.image} alt={blog.title} />
        </div>

        {/* Blog Content */}
        <div className="blog-content-wrapper">
          <article className="blog-article">
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Tags */}
            <div className="blog-tags-section">
              <h4><i className="fas fa-tags"></i> {t('blogs.tags')}:</h4>
              <div className="tags-list">
                {blog.tags.map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
            </div>

            {/* Share Again */}
            <div className="blog-share-footer">
              <h4>{t('blogs.detail.shareFooter')}</h4>
              <div className="share-buttons-large">
                <button onClick={() => handleShare('facebook')} className="share-btn-large facebook">
                  <i className="fab fa-facebook-f"></i>
                  {t('blogs.detail.shareOn')} Facebook
                </button>
                <button onClick={() => handleShare('twitter')} className="share-btn-large twitter">
                  <i className="fab fa-twitter"></i>
                  {t('blogs.detail.shareOn')} Twitter
                </button>
                <button onClick={() => handleShare('whatsapp')} className="share-btn-large whatsapp">
                  <i className="fab fa-whatsapp"></i>
                  {t('blogs.detail.shareOn')} WhatsApp
                </button>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="blog-sidebar">
            {/* Quick Links */}
            <div className="sidebar-card">
              <h3><i className="fas fa-link"></i> {t('blogs.detail.quickLinks')}</h3>
              <div className="quick-links">
                <a href="https://voters.eci.gov.in" target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-external-link-alt"></i>
                  {t('blogs.detail.voterRegistration')}
                </a>
                <a href="https://nvsp.in" target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-external-link-alt"></i>
                  {t('blogs.detail.checkStatus')}
                </a>
                <button onClick={() => navigate('/voter-poll')} className="quick-link-btn">
                  <i className="fas fa-vote-yea"></i>
                  {t('blogs.detail.voterPoll')}
                </button>
                <button onClick={() => navigate('/results')} className="quick-link-btn">
                  <i className="fas fa-chart-line"></i>
                  {t('blogs.detail.liveResults')}
                </button>
              </div>
            </div>

            {/* Important Reminder */}
            <div className="sidebar-card reminder-card">
              <i className="fas fa-exclamation-circle"></i>
              <h3>{t('blogs.detail.remember')}</h3>
              <p>{t('blogs.detail.reminderText')}</p>
              <div className="reminder-stats">
                <div className="reminder-stat">
                  <strong>7 AM - 6 PM</strong>
                  <span>{t('blogs.detail.votingHours')}</span>
                </div>
                <div className="reminder-stat">
                  <strong>243</strong>
                  <span>{t('blogs.detail.constituencies')}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <div className="related-section">
            <h2>
              <i className="fas fa-newspaper"></i>
              {t('blogs.relatedArticles')}
            </h2>
            <div className="related-grid">
              {relatedBlogs.map(relatedBlog => (
                <div
                  key={relatedBlog.id}
                  className="related-card"
                  onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
                >
                  <div className="related-image">
                    <img src={relatedBlog.image} alt={relatedBlog.title} />
                  </div>
                  <div className="related-content">
                    <span className="category-tag">{relatedBlog.category}</span>
                    <h3>{relatedBlog.title}</h3>
                    <p>{relatedBlog.excerpt}</p>
                    <span className="read-more">
                      {t('blogs.readMore')} <i className="fas fa-arrow-right"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Back to Blog */}
        <div className="back-to-blog">
          <button onClick={() => navigate('/blog')} className="back-btn">
            <i className="fas fa-arrow-left"></i>
            {t('blogs.backToBlogs')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
